import React from "react";
import { AssignmentTurnedIn, Assignment } from "@mui/icons-material";
import moment from "moment";
import { TimelineEvent } from "./Timeline";

export interface TimelineDetailProps {
  events: TimelineEvent[];
  currentStatus?: string;
  muiIcon?: typeof AssignmentTurnedIn;
  variant?: "card" | "list";
  showAllSteps?: boolean;
}

export const TimelineDetail: React.FC<TimelineDetailProps> = ({
  events,
  currentStatus,
  muiIcon: MuiIcon = AssignmentTurnedIn,
  variant = "card",
  showAllSteps = false,
}) => {
  const currentIndex = events.findIndex((e) => e.status === currentStatus);

  // Filter to show only completed steps unless showAllSteps is true
  const displayEvents = (showAllSteps
    ? events
    : events.filter((_, index) => index <= currentIndex)
  ).filter((e) => e.status !== "Rejected" || currentStatus === "Rejected");

  if (variant === "list") {
    return (
      <div className="space-y-2 sm:space-y-3">
        {displayEvents.map((event, index) => {
          const isActive = event.status === currentStatus;
          const isCompleted = currentIndex > index || event.completed;

          return (
            <div key={index} className="flex items-start space-x-3 sm:space-x-4">
              {/* Icon circle */}
              <div
                className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-200"
                style={{ backgroundColor: event.color + "15" }}
              >
              { isCompleted ? (
                <MuiIcon
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  style={{ color: event.color }}
                />
              ) : (
                <Assignment
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  style={{ color: event.color }}
                />
              )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1 sm:gap-0">
                  <div className="min-w-0">
                    <h4
                      className={`text-xs sm:text-sm font-semibold truncate ${
                        isActive ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {event.status.replaceAll("_", " ")}
                    </h4>
                    {event.userFullName && (
                      <p className="text-xs sm:text-sm text-gray-600 truncate">
                        {event.userFullName}
                      </p>
                    )}
                  </div>
                  {event.date && (
                    <div className="flex flex-col items-start sm:items-end text-left sm:text-right flex-shrink-0">
                      <time
                        className={`text-xs sm:text-sm font-medium whitespace-nowrap ${
                          isActive ? "text-gray-900" : "text-gray-500"
                        }`}
                      >
                        {moment(event.date).format("MMM D, YYYY")}
                      </time>
                      <time className="text-[10px] sm:text-xs text-gray-500 whitespace-nowrap">
                        {moment(event.date).format("h:mm A")}
                      </time>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Card variant
  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {displayEvents.map((event, index) => {
        const isActive = event.status === currentStatus;
        const isCompleted = currentIndex > index || event.completed;

        return (
          <div
            key={index}
            className="flex items-start space-x-3 sm:space-x-4 rounded-lg bg-white p-3 sm:p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
          >
            <div
              className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
              style={{ backgroundColor: event.color + "15" }}
            >
            { isCompleted ? (
              <MuiIcon
                className="w-5 h-5 sm:w-6 sm:h-6"
                style={{ color: event.color }}
              />
            ) : (
              <Assignment
                className="w-5 h-5 sm:w-6 sm:h-6"
                style={{ color: event.color }}
              />
            )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-0">
                <div className="min-w-0">
                  <h4
                    className={`text-sm sm:text-base font-semibold ${
                      isActive ? "text-gray-900" : "text-gray-500"
                    }`}
                  >
                    {event.status.replaceAll("_", " ")}
                  </h4>
                  {event.userFullName && (
                    <p className="mt-1 text-xs sm:text-sm text-gray-600">
                      {event.userFullName}
                    </p>
                  )}
                </div>
                {event.date && (
                  <div className="flex flex-col items-start sm:items-end text-left sm:text-right flex-shrink-0">
                    <time
                      className={`text-sm font-medium ${
                        isActive ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {moment(event.date).format("MMM D, YYYY")}
                    </time>
                    <time className="text-xs text-gray-500">
                      {moment(event.date).format("h:mm A")}
                    </time>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};